// Parsing + pending-state layer of the push -> chat navigation chain:
//
//   FCM -> native tap handler -> pending_push.json -> take_pending_push_navigation
//        -> [this module: parse + hold] -> usePushNavigation (navigate)
//        -> group page (open channel) -> useMessageJump (locate + scroll) -> highlight
//
// Deliberately free of Vue/Nuxt/Tauri imports so it stays a plain, directly unit-testable module
// (see `tests/e2e/push-navigation-parser.spec.ts`) — everything that needs the app (router,
// stores, toasts, the Tauri command) lives in `~/composables/usePushNavigation.ts` instead.

/** Discriminator the backend stamps on a new-message push — see `PushNavigationPayload.cs`. */
export const CHAT_MESSAGE_PUSH_TYPE = "chat_message"
/** Same, for an incoming friend request (`PushNavigationPayload.ForFriendRequest`). */
export const FRIEND_REQUEST_PUSH_TYPE = "friend_request"
/** Same, for an agent tool call waiting for the agent owner's approval. */
export const AGENT_APPROVAL_PUSH_TYPE = "agent_approval"

/** The whole navigation payload of a new-message push: which chat, which message. */
export interface ChatMessagePushTarget {
  type: typeof CHAT_MESSAGE_PUSH_TYPE
  /** Route segment — `/group/:groupId`. */
  groupId: string
  /** The chat itself: a group's text channel, i.e. what `?channel=` selects. */
  channelId: string
  /** The message to scroll to and flash once it's on screen. */
  messageId: string
}

/** A friend request push: who sent it and the request's own id. Opens the requests list. */
export interface FriendRequestPushTarget {
  type: typeof FRIEND_REQUEST_PUSH_TYPE
  /** The requester. */
  userId: string
  /** The friend-request record. */
  friendId: string
}

/** An agent asks its owner to approve a tool call. Opens the approvals list (/bots/approvals). */
export interface AgentApprovalPushTarget {
  type: typeof AGENT_APPROVAL_PUSH_TYPE
  /** The approval request record. */
  approvalId: string
  /** The agent (bot) that asked. */
  botId: string
}

export type PushNavigationTarget = ChatMessagePushTarget | FriendRequestPushTarget | AgentApprovalPushTarget

function readId(source: Record<string, unknown>, key: string): string | null {
  const value = source[key]
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Turns a raw FCM `data` block into a navigation target, or `null` for anything this app can't act
 * on: a different/absent `type`, a missing id, a non-object payload. Never throws — a malformed
 * push is a no-op, not an error path the user sees.
 */
export function parsePushNavigation(raw: unknown): PushNavigationTarget | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const source = raw as Record<string, unknown>

  switch (readId(source, "type")) {
    case CHAT_MESSAGE_PUSH_TYPE: {
      const groupId = readId(source, "groupId")
      const channelId = readId(source, "channelId")
      const messageId = readId(source, "messageId")
      if (!groupId || !channelId || !messageId) return null
      return { type: CHAT_MESSAGE_PUSH_TYPE, groupId, channelId, messageId }
    }
    case FRIEND_REQUEST_PUSH_TYPE: {
      const userId = readId(source, "userId")
      const friendId = readId(source, "friendId")
      if (!userId || !friendId) return null
      return { type: FRIEND_REQUEST_PUSH_TYPE, userId, friendId }
    }
    case AGENT_APPROVAL_PUSH_TYPE: {
      const approvalId = readId(source, "approvalId")
      const botId = readId(source, "botId")
      if (!approvalId || !botId) return null
      return { type: AGENT_APPROVAL_PUSH_TYPE, approvalId, botId }
    }
    default:
      return null
  }
}

/**
 * Identity of "this exact navigation" for idempotency purposes — chat + message, as required. Two
 * pushes for the same message (a retry, a duplicate delivery, a re-tap of a notification still in
 * the tray) collapse into one navigation.
 */
export function pushNavigationKey(target: PushNavigationTarget): string {
  switch (target.type) {
    case FRIEND_REQUEST_PUSH_TYPE:
      return `friend:${target.friendId}`
    case AGENT_APPROVAL_PUSH_TYPE:
      return `approval:${target.approvalId}`
    default:
      return `${target.channelId}:${target.messageId}`
  }
}

/**
 * The app's single internal route for "show me this message", which push is just one producer of —
 * an in-app notification, a message permalink, a Share or a future Universal/App Link can all
 * funnel through the same place. `?channel=&message=` is what the chat screen already restores
 * itself from, so this adds no new screen: `/group/:id` on desktop (`pages/group/[id].vue`), and on
 * mobile `/m-groups/:id` (`pages/m-groups/[id].vue`) — the same group + chat Drawers normal
 * navigation uses, so the chat opened from a push can be swiped away like any other, instead of
 * `/group/:id`'s bare inline chat that has no Drawer to swipe.
 */
export function pushNavigationRoute(target: PushNavigationTarget, options: { mobile?: boolean } = {}) {
  // A friend request lands on the requests tab of the friends list (`SidebarFriendsList` reads
  // `?tab=`), which the home screen shows on both platforms.
  if (target.type === FRIEND_REQUEST_PUSH_TYPE) {
    return { path: "/dashboard", query: { tab: "requests" } }
  }
  // An approval request lands on the full "awaiting your decision" list (pages/bots/approvals.vue).
  if (target.type === AGENT_APPROVAL_PUSH_TYPE) {
    return { path: "/bots/approvals" }
  }
  return {
    path: `${options.mobile ? "/m-groups" : "/group"}/${target.groupId}`,
    query: { channel: target.channelId, message: target.messageId },
  }
}

/**
 * Holds the target between "a push was tapped" and "the app is ready to navigate", which on a cold
 * start is several seconds and a whole auth/profile/SignalR bootstrap apart. Also the idempotency
 * ledger: a target that has already been navigated to is never navigated to a second time, however
 * many times its payload shows up again.
 *
 * A plain class (not a Pinia store or a `ref`) on purpose: nothing renders off it, and keeping it
 * framework-free is what makes the whole pending/dedupe contract unit-testable.
 */
export class PendingPushNavigationStore {
  private pending: PushNavigationTarget | null = null
  /** Bounded FIFO — this only needs to cover repeats within one app session. */
  private handled: string[] = []

  constructor(private readonly handledLimit = 20) {}

  /**
   * Records a tapped push as the one to act on. A newer tap always replaces an unprocessed older
   * one: with pushes A/B/C waiting, the user's tap on B is the intent, not whatever arrived last.
   * Already-handled targets are ignored outright.
   */
  set(target: PushNavigationTarget): boolean {
    if (this.hasHandled(target)) return false
    this.pending = target
    return true
  }

  peek(): PushNavigationTarget | null {
    return this.pending
  }

  /** Removes and returns the pending target — the caller now owns navigating to it. */
  take(): PushNavigationTarget | null {
    const target = this.pending
    this.pending = null
    return target
  }

  /** Marks a target as navigated to, so a repeat of the same payload is dropped by `set`. */
  markHandled(target: PushNavigationTarget) {
    const key = pushNavigationKey(target)
    if (this.handled.includes(key)) return
    this.handled.push(key)
    if (this.handled.length > this.handledLimit) this.handled.shift()
  }

  hasHandled(target: PushNavigationTarget): boolean {
    return this.handled.includes(pushNavigationKey(target))
  }

  /** Logout: neither the pending target nor the handled history survives a change of user. */
  clear() {
    this.pending = null
    this.handled = []
  }
}

export const pendingPushNavigation = new PendingPushNavigationStore()
